import React from "react";
import { Helmet } from "react-helmet";
import get from "lodash/get";
import difference from "lodash/difference";
import uniq from "lodash/uniq";
import { connect } from "react-redux";

import {
  Switch,
  Layout,
  Alert,
  Row,
  Col,
  Button,
  Checkbox,
  message,
} from "antd";

import AdminHeader from "../../components/AdminHeader/AdminHeader";
import Footer from "../../components/Footer/Footer";
import MyProfileForm from "../../components/MyProfileForm/MyProfileForm";
import ResetProfileForm from "../../components/ResetProfileForm/ResetProfileForm";
import UserProfileIdForm from "../../components/ResetProfileForm/UserProfileIdForm";

import {
  getQRCodeRequest,
  getAccountInfoRequest,
  getTwoFactorRequest,
  setTwoFactorRequest,
  updateTwoFactorMethodsRequest,
  getNotificationSettingsRequest,
  setNotificationSettingsRequest,
} from "../../api/requests";
import {
  getTwoFactorReadPermission,
  getTwoFactorUpdatePermission,
  getNotificationRandomizationSummaryPermission,
  getNotificationRandomizationClosedPermission,
  getNotificationConsentPermission,
  getNotificationValidateRandomizationPermission,
  getNotificationValidationDiffersFromRandomizationPermission,
  getNotificationValidationSaeOccurredPermission,
  getNotification2DayReminderPermission,
  getNotification7DayReminderPermission,
  getNotificationRandomizationDetailedPermission,
  getNotificationReportSummaryPermission,
  getNotificationNewUserWelcomePermission,
  getNotificationNewUserPermission,
  getNotificationNewSitePermission,
  getNotificationSuspendedSitePermission,
  getNotificationDeathPermission,
  getNotification90DayFollowUpCompletedPermission,
  getNotification90DayDeviationOccurredPermission,
  getNotificationDocumentPendingApprovalPermission,
  getNotificationTrainingCompletedPermission,
  getNotificationNewsletterPermission,
  getNotificationDocumentDocumentApprovedPermission,
  getUserToken,
  getNotificationSiteRandomizationStatusChangedPermission,
  getNotificationDocumentExpirePermission,
  getNotificationConsentMissingReportPermission,
  getNotificationUserStatusChangedPermission,
} from "../../reducers/user/selectors";

import isAuthenticated from "../../utils/isAuthenticated";
import classes from "./MyProfile.css";
import { logout, setAccountInfo } from "../../reducers/user/actions";

const { Content } = Layout;

class MyProfile extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      qrCode: null,
      authenticationEnabled: false,
      isAuthenticationRequest: true,
      authError: "",
      username: "",
      profile: null,
      isProfileEdit: false,
      isPasswordEdit: false,
      twoFaCheckedList: [],
      notificationsCheckedList: [],
      notificationOptions: [],
      profileCode: null,
      showProfileCodeForm: false,
    };
  }

  componentDidMount() {
    if (this.props.twoFactorReadPermission) {
      this.getQR();
    }
    this.getProfile();
    this.getNotifications();

    const notificationOptions = [];

    if (this.props.notificationRandomizationSummaryPermission) {
      notificationOptions.push({
        key: "notification_randomization_summary",
        value: "Notification randomization summary",
        group: "randomization",
      });
    }
    if (this.props.notificationRandomizationClosedPermission) {
      notificationOptions.push({
        key: "notification_randomization_closed",
        value: "Notification randomization closed",
        group: "randomization",
      });
    }
    if (this.props.notificationRandomizationDetailedPermission) {
      notificationOptions.push({
        key: "notification_randomization_detailed",
        value: "Notification randomization detailed",
        group: "randomization",
      });
    }
    if (this.props.notificationConsentPermission) {
      notificationOptions.push({
        key: "notification_consent",
        value: "Notification consent",
        group: "randomization",
      });
    }
    if (this.props.notificationValidateRandomizationPermission) {
      notificationOptions.push({
        key: "notification_validate_randomization",
        value: "Notification validate randomization",
        group: "randomization",
      });
    }
    if (this.props.notificationValidationDiffersFromRandomizationPermission) {
      notificationOptions.push({
        key: "notification_validation_differs_from_randomization",
        value: "Notification validation differs from randomization",
        group: "randomization",
      });
    }
    if (this.props.notificationValidationSaeOccurredPermission) {
      notificationOptions.push({
        key: "notification_validation_sae_occurred",
        value: "Notification validation sae occurred",
        group: "randomization",
      });
    }
    if (this.props.notification2DayReminderPermission) {
      notificationOptions.push({
        key: "notification_2_day_reminder",
        value: "Notification 2 day reminder",
        group: "randomization",
      });
    }
    if (this.props.notification7DayReminderPermission) {
      notificationOptions.push({
        key: "notification_7_day_reminder",
        value: "Notification 7 day reminder",
        group: "randomization",
      });
    }
    if (this.props.notificationReportSummaryPermission) {
      notificationOptions.push({
        key: "report_subject_summary",
        value: "Report subject summary",
        group: "randomization",
      });
    }

    if (this.props.notificationNewUserWelcomePermission) {
      notificationOptions.push({
        key: "notification_new_user_welcome",
        value: "Notification new user welcome",
        group: "admin",
      });
    }
    if (this.props.notificationNewUserPermission) {
      notificationOptions.push({
        key: "notification_new_user",
        value: "Notification new user",
        group: "admin",
      });
    }
    if (this.props.notificationNewSitePermission) {
      notificationOptions.push({
        key: "notification_new_site",
        value: "Notification new site",
        group: "admin",
      });
    }
    if (this.props.notificationSuspendedSitePermission) {
      notificationOptions.push({
        key: "notification_suspended_site",
        value: "Notification suspended site",
        group: "admin",
      });
    }
    if (this.props.notificationDeathPermission) {
      notificationOptions.push({
        key: "notification_death",
        value: "Notification death",
        group: "admin",
      });
    }
    if (this.props.notification90DayFollowUpCompletedPermission) {
      notificationOptions.push({
        key: "notification_90_day_follow_up_completed",
        value: "Notification 90 day follow up completed",
        group: "admin",
      });
    }
    if (this.props.notification90DayDeviationOccurredPermission) {
      notificationOptions.push({
        key: "notification_90_day_deviation_occurred",
        value: "Notification 90 day deviation occurred",
        group: "admin",
      });
    }
    if (this.props.notificationDocumentPendingApprovalPermission) {
      notificationOptions.push({
        key: "notification_document_pending_approval",
        value: "Notification document pending approval",
        group: "admin",
      });
    }
    if (this.props.notificationDocumentDocumentApprovedPermission) {
      notificationOptions.push({
        key: "notification_document_document_approved",
        value: "Notification document document approved",
        group: "admin",
      });
    }
    if (this.props.notificationTrainingCompletedPermission) {
      notificationOptions.push({
        key: "notification_training_completed",
        value: "Notification training completed",
        group: "admin",
      });
    }
    if (this.props.notificationNewsletterPermission) {
      notificationOptions.push({
        key: "notification_newsletter",
        value: "Notification newsletter",
        group: "admin",
      });
    }
    if (this.props.notificationSiteRandomizationStatusChangedPermission) {
      notificationOptions.push({
        key: "notification_site_randomization_status_changed",
        value: "Notification site randomization status changed",
        group: "admin",
      });
    }
    if (this.props.notificationDocumentExpirePermission) {
      notificationOptions.push({
        key: "notification_document_expire",
        value: "Notification document expire",
        group: "admin",
      });
    }
    if (this.props.notificationConsentMissingReportPermission) {
      notificationOptions.push({
        key: "notification_consent_missing_report",
        value: "Notification consent missing report",
        group: "admin",
      });
    }
    if (this.props.notificationUserStatusChangedPermission) {
      notificationOptions.push({
        key: "notification_user_status_changed",
        value: "Notification user status changed",
        group: "admin",
      });
    }
    this.setState({ notificationOptions });
  }

  getQR = async () => {
    try {
      const { data } = await getQRCodeRequest();
      this.setState({ qrCode: data });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }

    try {
      const {
        data: {
          is_2fa_enabled,
          username,
          is_2fa_email_enabled,
          is_2fa_sms_enabled,
          is_2fa_mobile_app_enabled,
        },
      } = await getTwoFactorRequest();
      const twoFaCheckedList = [];
      if (is_2fa_email_enabled) {
        twoFaCheckedList.push("Email");
      }
      if (is_2fa_sms_enabled) {
        twoFaCheckedList.push("SMS");
      }
      if (is_2fa_mobile_app_enabled) {
        twoFaCheckedList.push("Google Authenticator");
      }

      this.setState({
        authenticationEnabled: is_2fa_enabled,
        username,
        twoFaCheckedList,
      });
    } catch (e) {
      console.log(e);
    }

    this.setState({ isAuthenticationRequest: false });
  };

  getProfile = async () => {
    try {
      this.props.setAccountInfo(this.props.token);
      const { data } = await getAccountInfoRequest();
      this.setState({ profile: data });
    } catch (e) {
      console.log(e);
    }
  };

  getNotifications = async () => {
    try {
      const { data } = await getNotificationSettingsRequest();

      const notificationsCheckedList = [];
      Object.keys(data).forEach((item) => {
        if (data[item]) {
          notificationsCheckedList.push(
            item.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
          );
        }
      });

      this.setState({ notificationsCheckedList });
    } catch (e) {
      console.log(e);
    }
  };

  onChangeNotifications = (group) => async (notificationsGroup) => {
    let notificationsCheckedListFiltered = this.state.notificationOptions
      .filter(
        (opt) =>
          this.state.notificationsCheckedList.includes(opt.value) &&
          opt.group !== group
      )
      .map((item) => item.value);

    const notifications = uniq([
      ...notificationsCheckedListFiltered,
      ...notificationsGroup,
    ]);
    const { notificationOptions } = this.state;
    const notificationsCheckedList = {};

    notificationOptions.forEach((item) => {
      notificationsCheckedList[item.key] = notifications.includes(item.value);
    });

    try {
      await setNotificationSettingsRequest(notificationsCheckedList);
      this.setState({ notificationsCheckedList: notifications });
      message.success("Saved");
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.errors._schema[0]"));
      console.log(e);
    }
  };

  onChangeAuthentication = async (checked) => {
    this.setState({ isAuthenticationRequest: true });

    try {
      await setTwoFactorRequest(checked ? "enable" : "disable");
      this.setState({ authenticationEnabled: checked });
      message.success("Saved");
    } catch (e) {
      this.setState({ authError: get(e, "response.data.error") });
    }

    this.setState({ isAuthenticationRequest: false });
  };

  onProfileEdit = () => {
    this.setState({ isProfileEdit: true });
  };

  onProfileEditHide = () => {
    this.setState({ isProfileEdit: false });
  };

  onSubmitProfile = () => {
    this.getProfile();
    setTimeout(() => {
      this.onProfileEditHide();
    }, 1500);
  };

  onChange2FA = async (twoFaCheckedList) => {
    const updated2FA = {
      is_2fa_mobile_app_enabled: twoFaCheckedList.includes(
        "Google Authenticator"
      ),
      is_2fa_email_enabled: twoFaCheckedList.includes("Email"),
      is_2fa_sms_enabled: twoFaCheckedList.includes("SMS"),
    };

    try {
      await updateTwoFactorMethodsRequest(updated2FA);
      this.setState({ twoFaCheckedList });
      message.success("Saved");
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error(get(e, "response.data.errors._schema[0]"));
      console.log(e);
    }
  };

  onPasswordEditToggle = () => {
    this.setState((state) => ({ isPasswordEdit: !state.isPasswordEdit }));
  };

  onShowUserId = () => {
    this.setState({
      showProfileCodeForm: true,
    });
  };

  render() {
    const {
      qrCode,
      authenticationEnabled,
      isAuthenticationRequest,
      authError,
      profile,
      isProfileEdit,
      twoFaCheckedList,
      notificationsCheckedList,
      isPasswordEdit,
      notificationOptions,
      profileCode,
      showProfileCodeForm,
    } = this.state;
    const { twoFactorReadPermission, twoFactorUpdatePermission } = this.props;

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Helmet>
          <title>Trial Management Portal: My Profile</title>
        </Helmet>
        <Layout className={classes.parentContainer}>
          <AdminHeader />
          <Content className={classes.content}>
            <Row gutter={8}>
              <Col lg={12}>
                <h3 className="headline-default">My Profile</h3>
                {profile && (
                  <h4 className="headline-default">
                    Welcome,{" "}
                    {get(profile, "first_name")
                      ? `${get(profile, "first_name")} ${get(
                          profile,
                          "last_name"
                        )}`
                      : get(profile, "email")}
                  </h4>
                )}
                {profile && profile.password_expire_days && (
                  <p
                    style={{
                      color: "#555555",
                      fontSize: "14px",
                      marginTop: "-5px",
                    }}
                  >
                    {profile.password_expire_days} days until password expire
                  </p>
                )}
                <br />

                {twoFactorReadPermission && (
                  <div>
                    <h5 className="headline-default">
                      Enable 2 factor authentication
                    </h5>
                    <Switch
                      disabled={!twoFactorUpdatePermission}
                      checked={authenticationEnabled}
                      onChange={this.onChangeAuthentication}
                      loading={isAuthenticationRequest}
                    />
                    {authError && (
                      <div>
                        <br />
                        <Alert
                          message={authError}
                          type="error"
                          class={classes.alertStyle}
                        />
                      </div>
                    )}

                    <br />
                    <br />

                    {authenticationEnabled && (
                      <div>
                        <h5 className="headline-default">2FA type:</h5>
                        <Checkbox.Group
                          disabled={!twoFactorUpdatePermission}
                          options={["Google Authenticator", "Email", "SMS"]}
                          value={twoFaCheckedList}
                          onChange={this.onChange2FA}
                        />

                        <br />
                        <br />

                        <h5 className="headline-default">
                          QR-code for 2FA-mobile apps:
                        </h5>
                        <p>You can use Google Authenticator mobile app</p>
                        <div
                          className={classes.qrWrapper}
                          dangerouslySetInnerHTML={{ __html: qrCode }}
                        />
                        <br />
                        <br />
                      </div>
                    )}
                  </div>
                )}

                <h5 className="headline-default">
                  Randomization platform notifications:
                </h5>
                <Checkbox.Group
                  className={classes.notificationGroup}
                  options={notificationOptions
                    .filter((item) => item.group === "randomization")
                    .map((item) => item.value)}
                  value={notificationsCheckedList}
                  onChange={this.onChangeNotifications("randomization")}
                />
                <br />

                <h5 className="headline-default">Admin notifications:</h5>
                <Checkbox.Group
                  className={classes.notificationGroup}
                  options={notificationOptions
                    .filter((item) => item.group === "admin")
                    .map((item) => item.value)}
                  value={notificationsCheckedList}
                  onChange={this.onChangeNotifications("admin")}
                />
              </Col>
              <Col lg={12}>
                {!isProfileEdit ? (
                  <div className={classes.profileDetailsWrappers}>
                    <h3 className="headline-default">Profile details</h3>
                    <p>
                      <b>Email:</b> {get(profile, "email")}
                    </p>
                    <p>
                      <b>First name:</b>{" "}
                      {get(profile, "first_name") || "not indicated"}
                    </p>
                    <p>
                      <b>Last name:</b>{" "}
                      {get(profile, "last_name") || "not indicated"}
                    </p>
                    <p>
                      <b>Timezone:</b>{" "}
                      {get(profile, "time_zone") || "not indicated"}
                    </p>
                    <p>
                      <b>Short Timezone:</b>{" "}
                      {get(profile, "short_tz") || "not indicated"}
                    </p>
                    <p>
                      <b>Phone number:</b>{" "}
                      {get(profile, "phone_number") || "not indicated"}
                    </p>
                    <p>
                      <Button
                        type="primary"
                        size="large"
                        onClick={this.onProfileEdit}
                      >
                        Edit
                      </Button>
                    </p>
                  </div>
                ) : (
                  <MyProfileForm
                    {...profile}
                    onCancel={this.onProfileEditHide}
                    onSubmit={this.onSubmitProfile}
                  />
                )}

                {!isProfileEdit && isPasswordEdit ? (
                  <div style={{ maxWidth: "420px" }}>
                    <ResetProfileForm
                      myProfile
                      toggleFormVisibility={this.onPasswordEditToggle}
                    />
                  </div>
                ) : (
                  <div>
                    <Button
                      type="primary"
                      size="large"
                      onClick={this.onPasswordEditToggle}
                    >
                      Edit password
                    </Button>
                  </div>
                )}

                <br />

                {profileCode ? (
                  <div style={{ maxWidth: "420px" }}>
                    <h5 className="headline-default">User id</h5>
                    {profileCode}
                  </div>
                ) : (
                  <div style={{ maxWidth: "420px" }}>
                    {showProfileCodeForm ? (
                      <UserProfileIdForm
                        onSubmit={(profileCode) => {
                          this.setState({ profileCode });
                        }}
                      />
                    ) : (
                      <Button
                        type="primary"
                        size="large"
                        onClick={this.onShowUserId}
                      >
                        Show user ID
                      </Button>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Content>
          <Footer noAdminLink />
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  token: getUserToken(state),
  twoFactorReadPermission: getTwoFactorReadPermission(state),
  twoFactorUpdatePermission: getTwoFactorUpdatePermission(state),
  notificationRandomizationSummaryPermission: getNotificationRandomizationSummaryPermission(
    state
  ),
  notificationRandomizationClosedPermission: getNotificationRandomizationClosedPermission(
    state
  ),
  notificationRandomizationDetailedPermission: getNotificationRandomizationDetailedPermission(
    state
  ),
  notificationConsentPermission: getNotificationConsentPermission(state),
  notificationValidateRandomizationPermission: getNotificationValidateRandomizationPermission(
    state
  ),
  notificationValidationDiffersFromRandomizationPermission: getNotificationValidationDiffersFromRandomizationPermission(
    state
  ),
  notificationValidationSaeOccurredPermission: getNotificationValidationSaeOccurredPermission(
    state
  ),
  notification2DayReminderPermission: getNotification2DayReminderPermission(
    state
  ),
  notification7DayReminderPermission: getNotification7DayReminderPermission(
    state
  ),
  notificationReportSummaryPermission: getNotificationReportSummaryPermission(
    state
  ),

  notificationNewUserWelcomePermission: getNotificationNewUserWelcomePermission(
    state
  ),
  notificationNewUserPermission: getNotificationNewUserPermission(state),
  notificationNewSitePermission: getNotificationNewSitePermission(state),
  notificationSuspendedSitePermission: getNotificationSuspendedSitePermission(
    state
  ),
  notificationDeathPermission: getNotificationDeathPermission(state),
  notification90DayFollowUpCompletedPermission: getNotification90DayFollowUpCompletedPermission(
    state
  ),
  notification90DayDeviationOccurredPermission: getNotification90DayDeviationOccurredPermission(
    state
  ),
  notificationDocumentPendingApprovalPermission: getNotificationDocumentPendingApprovalPermission(
    state
  ),
  notificationTrainingCompletedPermission: getNotificationTrainingCompletedPermission(
    state
  ),
  notificationNewsletterPermission: getNotificationNewsletterPermission(state),
  notificationDocumentDocumentApprovedPermission: getNotificationDocumentDocumentApprovedPermission(
    state
  ),
  notificationSiteRandomizationStatusChangedPermission: getNotificationSiteRandomizationStatusChangedPermission(
    state
  ),
  notificationDocumentExpirePermission: getNotificationDocumentExpirePermission(
    state
  ),
  notificationConsentMissingReportPermission: getNotificationConsentMissingReportPermission(
    state
  ),
  notificationUserStatusChangedPermission: getNotificationUserStatusChangedPermission(
    state
  ),
});

const mapDispatchToProps = { logout, setAccountInfo };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(MyProfile)
);



// WEBPACK FOOTER //
// ./src/containers/MyProfile/MyProfile.js